import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Users, UserPlus, FileText, Plus, Trash2, Edit, Mail, Send,
  LogOut, Loader2, RefreshCw, CheckCircle, XCircle, Clock, Eye,
  FileCheck, FilePlus, Briefcase, Building, AlertCircle, Menu, X,
  LayoutDashboard, Settings, ChevronDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HRAdminPortal = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const [employeeForm, setEmployeeForm] = useState({
    name: '', email: '', phone: '', date_of_joining: '', position: '',
    role_template_id: '', department: '', ctc: '', location: '', reports_to: '', is_fresher: false
  });
  
  const [roleForm, setRoleForm] = useState({
    role_name: '', department: '', responsibilities: '', kpis: '',
    reports_to: '', default_ctc: '', probation_months: 3, notice_period_days: 30
  });
  
  const [testEmailForm, setTestEmailForm] = useState({ to_email: '', subject: '', body: '' });

  const userName = localStorage.getItem('hrUserName') || 'HR Manager';

  useEffect(() => {
    const role = localStorage.getItem('userRole');
    if (role !== 'hr') {
      navigate('/hr/login');
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, employeesRes, templatesRes] = await Promise.all([
        fetch(`${API_URL}/api/hr/dashboard-stats`),
        fetch(`${API_URL}/api/hr/employees`),
        fetch(`${API_URL}/api/hr/role-templates`)
      ]);

      const [statsData, employeesData, templatesData] = await Promise.all([
        statsRes.json(), employeesRes.json(), templatesRes.json()
      ]);

      if (statsData.success) setStats(statsData.stats);
      if (employeesData.success) setEmployees(employeesData.employees);
      if (templatesData.success) setRoleTemplates(templatesData.templates);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('hrToken');
    localStorage.removeItem('hrUserId');
    localStorage.removeItem('hrUserName');
    localStorage.removeItem('userRole');
    navigate('/hr/login');
  };

  // Create Employee
  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        ...employeeForm,
        ctc: parseFloat(employeeForm.ctc),
        role_template_id: employeeForm.role_template_id === 'none' ? '' : employeeForm.role_template_id
      };
      
      const response = await fetch(`${API_URL}/api/hr/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        alert(`Employee registered! Offer letter sent to ${employeeForm.email}\n\nAcceptance Link: ${data.acceptance_link}`);
        setShowEmployeeModal(false);
        resetEmployeeForm();
        fetchAllData();
      } else {
        alert(data.detail || 'Error creating employee');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error creating employee');
    } finally {
      setIsSaving(false);
    }
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      name: '', email: '', phone: '', date_of_joining: '', position: '',
      role_template_id: '', department: '', ctc: '', location: '', reports_to: '', is_fresher: false
    });
  };

  // Create Role Template
  const handleCreateRole = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        ...roleForm,
        responsibilities: roleForm.responsibilities.split('\n').filter(r => r.trim()),
        kpis: roleForm.kpis.split('\n').filter(k => k.trim()),
        default_ctc: parseFloat(roleForm.default_ctc) || 0,
        probation_months: parseInt(roleForm.probation_months),
        notice_period_days: parseInt(roleForm.notice_period_days)
      };
      
      const url = editingRole 
        ? `${API_URL}/api/hr/role-templates/${editingRole.id}`
        : `${API_URL}/api/hr/role-templates`;
      
      const response = await fetch(url, {
        method: editingRole ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        setShowRoleModal(false);
        resetRoleForm();
        fetchAllData();
      } else {
        alert(data.detail || 'Error saving role template');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const resetRoleForm = () => {
    setRoleForm({
      role_name: '', department: '', responsibilities: '', kpis: '',
      reports_to: '', default_ctc: '', probation_months: 3, notice_period_days: 30
    });
    setEditingRole(null);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      role_name: role.role_name,
      department: role.department,
      responsibilities: role.responsibilities?.join('\n') || '',
      kpis: role.kpis?.join('\n') || '',
      reports_to: role.reports_to,
      default_ctc: role.default_ctc?.toString() || '',
      probation_months: role.probation_months || 3,
      notice_period_days: role.notice_period_days || 30
    });
    setShowRoleModal(true);
  };

  const handleDeleteRole = async (id) => {
    if (!window.confirm('Delete this role template?')) return;
    await fetch(`${API_URL}/api/hr/role-templates/${id}`, { method: 'DELETE' });
    fetchAllData();
  };

  // Employee Actions
  const handleViewEmployee = async (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeDetails(true);
  };

  const handleVerifyDocuments = async (employeeId) => {
    if (!window.confirm('Verify all documents for this employee?')) return;
    try {
      const response = await fetch(`${API_URL}/api/hr/employees/${employeeId}/verify-documents`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        alert('Documents verified successfully!');
        fetchAllData();
        setShowEmployeeDetails(false);
      } else {
        alert(data.detail || 'Error verifying documents');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSendAgreement = async (employeeId) => {
    if (!window.confirm('Send employment agreement to this employee?')) return;
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/hr/employees/${employeeId}/send-agreement`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        alert('Employment agreement sent successfully!');
        fetchAllData();
        setShowEmployeeDetails(false);
      } else {
        alert(data.detail || 'Error sending agreement');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApproveEmployment = async (employeeId) => {
    if (!window.confirm('Approve employment for this employee? This will mark them as active.')) return;
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/hr/employees/${employeeId}/approve-employment`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        alert('Employment approved! Welcome email sent to employee.');
        fetchAllData();
        setShowEmployeeDetails(false);
      } else {
        alert(data.detail || 'Error approving employment');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResendOffer = async (employeeId) => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/hr/resend-offer/${employeeId}`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        alert('Offer letter resent successfully!');
      } else {
        alert(data.detail || 'Error resending offer');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Delete this employee record?')) return;
    await fetch(`${API_URL}/api/hr/employees/${id}`, { method: 'DELETE' });
    fetchAllData();
  };

  // Test Email
  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/hr/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testEmailForm)
      });
      const data = await response.json();
      if (data.success) {
        alert('Test email sent successfully!');
        setShowTestEmailModal(false);
        setTestEmailForm({ to_email: '', subject: '', body: '' });
      } else {
        alert(data.detail || 'Error sending email');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error sending email');
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      offer_sent: 'bg-yellow-100 text-yellow-700',
      offer_accepted: 'bg-blue-100 text-blue-700',
      documents_pending: 'bg-orange-100 text-orange-700',
      documents_uploaded: 'bg-purple-100 text-purple-700',
      documents_verified: 'bg-indigo-100 text-indigo-700',
      agreement_sent: 'bg-cyan-100 text-cyan-700',
      agreement_signed: 'bg-teal-100 text-teal-700',
      active: 'bg-green-100 text-green-700',
      terminated: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      offer_sent: 'Offer Sent',
      offer_accepted: 'Offer Accepted',
      documents_pending: 'Docs Pending',
      documents_uploaded: 'Docs Uploaded',
      documents_verified: 'Docs Verified',
      agreement_sent: 'Agreement Sent',
      agreement_signed: 'Agreement Signed',
      active: 'Active',
      terminated: 'Terminated'
    };
    return labels[status] || status;
  };

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'employees', label: 'Employees', icon: Users },
    { id: 'roles', label: 'Role Templates', icon: Briefcase },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100" data-testid="loading-spinner">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:inset-auto`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-white font-bold text-lg">HR Portal</h1>
                <p className="text-slate-400 text-xs">STEAM-IE</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setMobileSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === item.id 
                    ? 'bg-teal-600 text-white' 
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold">
                {userName.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{userName}</p>
                <p className="text-slate-400 text-xs">HR Admin</p>
              </div>
            </div>
            <Button onClick={handleLogout} variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setMobileSidebarOpen(true)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold text-gray-900 capitalize">{activeTab}</h2>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => setShowTestEmailModal(true)} variant="outline" size="sm">
                <Mail className="w-4 h-4 mr-2" /> Test Email
              </Button>
              <Button onClick={fetchAllData} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-4 lg:p-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Total Employees</p>
                        <p className="text-3xl font-bold">{stats.total_employees || 0}</p>
                      </div>
                      <Users className="w-10 h-10 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Active</p>
                        <p className="text-3xl font-bold">{stats.active_employees || 0}</p>
                      </div>
                      <CheckCircle className="w-10 h-10 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-yellow-100 text-sm">Pending Actions</p>
                        <p className="text-3xl font-bold">{(stats.pending_offers || 0) + (stats.pending_documents || 0) + (stats.pending_approvals || 0)}</p>
                      </div>
                      <Clock className="w-10 h-10 text-yellow-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Role Templates</p>
                        <p className="text-3xl font-bold">{stats.total_templates || 0}</p>
                      </div>
                      <Briefcase className="w-10 h-10 text-purple-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => { resetEmployeeForm(); setShowEmployeeModal(true); }} className="bg-teal-600 hover:bg-teal-700">
                      <UserPlus className="w-4 h-4 mr-2" /> Register Employee
                    </Button>
                    <Button onClick={() => { resetRoleForm(); setShowRoleModal(true); }} variant="outline">
                      <Plus className="w-4 h-4 mr-2" /> Add Role Template
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Employees */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Recent Employees</CardTitle>
                  <Button variant="link" onClick={() => setActiveTab('employees')}>View All</Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {employees.slice(0, 5).map((emp) => (
                      <div key={emp.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 font-bold">
                            {emp.name?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium">{emp.name}</p>
                            <p className="text-sm text-gray-500">{emp.position}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(emp.status)}`}>
                          {getStatusLabel(emp.status)}
                        </span>
                      </div>
                    ))}
                    {employees.length === 0 && (
                      <p className="text-center text-gray-500 py-4">No employees yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Employees Tab */}
          {activeTab === 'employees' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>All Employees</CardTitle>
                <Button onClick={() => { resetEmployeeForm(); setShowEmployeeModal(true); }} className="bg-teal-600 hover:bg-teal-700" data-testid="add-employee-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Register Employee
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-3 px-2">Name</th>
                        <th className="py-3 px-2">Email</th>
                        <th className="py-3 px-2">Position</th>
                        <th className="py-3 px-2">Department</th>
                        <th className="py-3 px-2">DOJ</th>
                        <th className="py-3 px-2">Status</th>
                        <th className="py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map((emp) => (
                        <tr key={emp.id} className="border-b hover:bg-gray-50" data-testid={`employee-row-${emp.id}`}>
                          <td className="py-3 px-2 font-medium">{emp.name}</td>
                          <td className="py-3 px-2">{emp.email}</td>
                          <td className="py-3 px-2">{emp.position}</td>
                          <td className="py-3 px-2">{emp.department}</td>
                          <td className="py-3 px-2">{emp.date_of_joining}</td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(emp.status)}`}>
                              {getStatusLabel(emp.status)}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleViewEmployee(emp)} data-testid={`view-employee-${emp.id}`}>
                                <Eye className="w-3 h-3" />
                              </Button>
                              {emp.status === 'offer_sent' && (
                                <Button size="sm" variant="outline" onClick={() => handleResendOffer(emp.id)} title="Resend Offer">
                                  <Send className="w-3 h-3" />
                                </Button>
                              )}
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteEmployee(emp.id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {employees.length === 0 && (
                    <p className="text-center py-8 text-gray-500">No employees yet. Register your first employee!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Roles Tab */}
          {activeTab === 'roles' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Role Templates</CardTitle>
                <Button onClick={() => { resetRoleForm(); setShowRoleModal(true); }} className="bg-teal-600 hover:bg-teal-700" data-testid="add-role-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Role
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {roleTemplates.map((role) => (
                    <div key={role.id} className="p-4 border rounded-xl hover:shadow-lg transition-shadow" data-testid={`role-card-${role.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold text-lg">{role.role_name}</h4>
                          <p className="text-sm text-gray-500">{role.department}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleEditRole(role)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteRole(role.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm space-y-2">
                        <p><strong>Reports to:</strong> {role.reports_to}</p>
                        <p><strong>Default CTC:</strong> ₹{role.default_ctc?.toLocaleString()}</p>
                        <p><strong>Probation:</strong> {role.probation_months} months</p>
                        <p><strong>Notice:</strong> {role.notice_period_days} days</p>
                      </div>
                    </div>
                  ))}
                  {roleTemplates.length === 0 && (
                    <p className="col-span-3 text-center py-8 text-gray-500">No role templates yet. Add your first role!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <Card>
              <CardHeader>
                <CardTitle>Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-2">SMTP Configuration</h4>
                    <p className="text-sm text-gray-600">Email: hr@steamie.net</p>
                    <p className="text-sm text-gray-600">Server: send.one.com</p>
                  </div>
                  <Button onClick={() => setShowTestEmailModal(true)} variant="outline">
                    <Mail className="w-4 h-4 mr-2" /> Send Test Email
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Add Employee Modal */}
      <Dialog open={showEmployeeModal} onOpenChange={setShowEmployeeModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Register New Employee</DialogTitle>
            <DialogDescription>Enter employee details to send offer letter</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateEmployee} className="space-y-4" data-testid="employee-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={employeeForm.name} onChange={(e) => setEmployeeForm({...employeeForm, name: e.target.value})} required data-testid="employee-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={employeeForm.email} onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})} required data-testid="employee-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={employeeForm.phone} onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})} data-testid="employee-phone" />
              </div>
              <div>
                <Label>Date of Joining *</Label>
                <Input type="date" value={employeeForm.date_of_joining} onChange={(e) => setEmployeeForm({...employeeForm, date_of_joining: e.target.value})} required data-testid="employee-doj" />
              </div>
              <div>
                <Label>Position *</Label>
                <Input value={employeeForm.position} onChange={(e) => setEmployeeForm({...employeeForm, position: e.target.value})} required data-testid="employee-position" />
              </div>
              <div>
                <Label>Role Template</Label>
                <Select value={employeeForm.role_template_id || 'none'} onValueChange={(v) => setEmployeeForm({...employeeForm, role_template_id: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="employee-role-template"><SelectValue placeholder="Select role template" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {roleTemplates.map(role => (
                      <SelectItem key={role.id} value={role.id}>{role.role_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Department *</Label>
                <Input value={employeeForm.department} onChange={(e) => setEmployeeForm({...employeeForm, department: e.target.value})} required data-testid="employee-department" />
              </div>
              <div>
                <Label>CTC (Annual) *</Label>
                <Input type="number" value={employeeForm.ctc} onChange={(e) => setEmployeeForm({...employeeForm, ctc: e.target.value})} required data-testid="employee-ctc" />
              </div>
              <div>
                <Label>Location *</Label>
                <Input value={employeeForm.location} onChange={(e) => setEmployeeForm({...employeeForm, location: e.target.value})} required data-testid="employee-location" />
              </div>
              <div>
                <Label>Reports To *</Label>
                <Input value={employeeForm.reports_to} onChange={(e) => setEmployeeForm({...employeeForm, reports_to: e.target.value})} required data-testid="employee-reports-to" />
              </div>
              <div className="col-span-2 flex items-center space-x-2">
                <Checkbox 
                  id="is_fresher" 
                  checked={employeeForm.is_fresher} 
                  onCheckedChange={(checked) => setEmployeeForm({...employeeForm, is_fresher: checked})}
                  data-testid="employee-fresher"
                />
                <Label htmlFor="is_fresher">Fresher (No previous work experience)</Label>
              </div>
            </div>
            <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={isSaving} data-testid="submit-employee">
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Register & Send Offer Letter
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Role Modal */}
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role Template' : 'Add Role Template'}</DialogTitle>
            <DialogDescription>Define role responsibilities and KPIs</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateRole} className="space-y-4" data-testid="role-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role Name *</Label>
                <Input value={roleForm.role_name} onChange={(e) => setRoleForm({...roleForm, role_name: e.target.value})} required data-testid="role-name" />
              </div>
              <div>
                <Label>Department *</Label>
                <Input value={roleForm.department} onChange={(e) => setRoleForm({...roleForm, department: e.target.value})} required data-testid="role-department" />
              </div>
              <div>
                <Label>Reports To *</Label>
                <Input value={roleForm.reports_to} onChange={(e) => setRoleForm({...roleForm, reports_to: e.target.value})} required data-testid="role-reports-to" />
              </div>
              <div>
                <Label>Default CTC (Annual)</Label>
                <Input type="number" value={roleForm.default_ctc} onChange={(e) => setRoleForm({...roleForm, default_ctc: e.target.value})} data-testid="role-ctc" />
              </div>
              <div>
                <Label>Probation (Months)</Label>
                <Input type="number" value={roleForm.probation_months} onChange={(e) => setRoleForm({...roleForm, probation_months: e.target.value})} data-testid="role-probation" />
              </div>
              <div>
                <Label>Notice Period (Days)</Label>
                <Input type="number" value={roleForm.notice_period_days} onChange={(e) => setRoleForm({...roleForm, notice_period_days: e.target.value})} data-testid="role-notice" />
              </div>
            </div>
            <div>
              <Label>Responsibilities (one per line) *</Label>
              <Textarea 
                value={roleForm.responsibilities} 
                onChange={(e) => setRoleForm({...roleForm, responsibilities: e.target.value})} 
                rows={5}
                placeholder="Enter each responsibility on a new line..."
                required
                data-testid="role-responsibilities"
              />
            </div>
            <div>
              <Label>KPIs (one per line)</Label>
              <Textarea 
                value={roleForm.kpis} 
                onChange={(e) => setRoleForm({...roleForm, kpis: e.target.value})} 
                rows={4}
                placeholder="Enter each KPI on a new line..."
                data-testid="role-kpis"
              />
            </div>
            <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={isSaving} data-testid="submit-role">
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingRole ? 'Update Role Template' : 'Create Role Template'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Test Email Modal */}
      <Dialog open={showTestEmailModal} onOpenChange={setShowTestEmailModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>Test SMTP configuration</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendTestEmail} className="space-y-4">
            <div>
              <Label>To Email *</Label>
              <Input type="email" value={testEmailForm.to_email} onChange={(e) => setTestEmailForm({...testEmailForm, to_email: e.target.value})} required />
            </div>
            <div>
              <Label>Subject *</Label>
              <Input value={testEmailForm.subject} onChange={(e) => setTestEmailForm({...testEmailForm, subject: e.target.value})} required />
            </div>
            <div>
              <Label>Body *</Label>
              <Textarea value={testEmailForm.body} onChange={(e) => setTestEmailForm({...testEmailForm, body: e.target.value})} required />
            </div>
            <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Test Email
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Employee Details Modal */}
      <Dialog open={showEmployeeDetails} onOpenChange={setShowEmployeeDetails}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Employee Details</DialogTitle>
          </DialogHeader>
          {selectedEmployee && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Name</Label>
                  <p className="font-medium">{selectedEmployee.name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Email</Label>
                  <p className="font-medium">{selectedEmployee.email}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Position</Label>
                  <p className="font-medium">{selectedEmployee.position}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Department</Label>
                  <p className="font-medium">{selectedEmployee.department}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Location</Label>
                  <p className="font-medium">{selectedEmployee.location}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Date of Joining</Label>
                  <p className="font-medium">{selectedEmployee.date_of_joining}</p>
                </div>
                <div>
                  <Label className="text-gray-500">CTC</Label>
                  <p className="font-medium">₹{selectedEmployee.ctc?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(selectedEmployee.status)}`}>
                    {getStatusLabel(selectedEmployee.status)}
                  </span>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Uploaded Documents
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {['cv', 'aadhar', 'pan', 'relieving_letter'].map(docType => (
                    <div key={docType} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="capitalize">{docType.replace('_', ' ')}</span>
                      {selectedEmployee.documents?.[docType] ? (
                        <a href={`${API_URL}${selectedEmployee.documents[docType]}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline flex items-center gap-1">
                          <Eye className="w-4 h-4" /> View
                        </a>
                      ) : (
                        <span className="text-gray-400 text-sm">Not uploaded</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Bank Details if available */}
              {selectedEmployee.documents?.bank_details && (
                <div className="bg-teal-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Bank Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><strong>Account Holder:</strong> {selectedEmployee.documents.bank_details.account_holder_name}</p>
                    <p><strong>Account No:</strong> {selectedEmployee.documents.bank_details.account_number}</p>
                    <p><strong>IFSC:</strong> {selectedEmployee.documents.bank_details.ifsc_code}</p>
                    <p><strong>Bank:</strong> {selectedEmployee.documents.bank_details.bank_name}</p>
                  </div>
                </div>
              )}

              {/* Actions based on status */}
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                {selectedEmployee.status === 'documents_uploaded' && (
                  <Button onClick={() => handleVerifyDocuments(selectedEmployee.id)} className="bg-purple-600 hover:bg-purple-700" disabled={isSaving}>
                    <FileCheck className="w-4 h-4 mr-2" /> Verify Documents
                  </Button>
                )}
                {selectedEmployee.status === 'documents_verified' && (
                  <Button onClick={() => handleSendAgreement(selectedEmployee.id)} className="bg-cyan-600 hover:bg-cyan-700" disabled={isSaving}>
                    <Send className="w-4 h-4 mr-2" /> Send Agreement
                  </Button>
                )}
                {selectedEmployee.status === 'agreement_signed' && (
                  <Button onClick={() => handleApproveEmployment(selectedEmployee.id)} className="bg-green-600 hover:bg-green-700" disabled={isSaving}>
                    <CheckCircle className="w-4 h-4 mr-2" /> Approve Employment
                  </Button>
                )}
                {selectedEmployee.status === 'offer_sent' && (
                  <Button onClick={() => handleResendOffer(selectedEmployee.id)} variant="outline" disabled={isSaving}>
                    <Send className="w-4 h-4 mr-2" /> Resend Offer
                  </Button>
                )}
                {selectedEmployee.signed_agreement_url && (
                  <a href={`${API_URL}${selectedEmployee.signed_agreement_url}`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline">
                      <FileText className="w-4 h-4 mr-2" /> View Signed Agreement
                    </Button>
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRAdminPortal;
